import { Calculator, GraduationCap, BookOpen } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { useEffect, useRef, useState } from "react";

const schoolSystems = [
  { name: "Common Core", icon: "school-systems/CommonCore.svg" },
  { name: "Georgia", icon: "school-systems/Georgia.svg" },
  { name: "Florida", icon: "school-systems/Florida.svg" },
  { name: "Maryland", icon: "school-systems/MaryLand.svg" },
  { name: "New York", icon: "school-systems/NewYork.svg" },
  { name: "Oklahoma", icon: "school-systems/Oklahoma.svg" },
  { name: "Texas", icon: "school-systems/Texas.svg" },
];

const gradeLevels = [
  { 
    name: "Preschool / Kindergarten", 
    icons: ["grade-icons/Pre-K.svg", "grade-icons/Kindergarten.svg"], 
    grades: "Pre-K–K" 
  },
  { 
    name: "Elementary School", 
    icons: [
      "grade-icons/First_Grade.svg", 
      "grade-icons/Second_Grade.svg", 
      "grade-icons/Third_Grade.svg", 
      "grade-icons/Fourth_Grade.svg", 
      "grade-icons/Fifth_Grade.svg"
    ], 
    grades: "1-5" 
  },
  { 
    name: "Middle School", 
    icons: [
      "grade-icons/Sixth_Grade.svg", 
      "grade-icons/Seventh_Grade.svg", 
      "grade-icons/Eighth_Grade.svg"
    ], 
    grades: "6-8" 
  },
  { 
    name: "High School", 
    icons: [
      "grade-icons/Algebra_1.svg", 
      "grade-icons/Algebra_2.svg", 
      "grade-icons/PreCalculus.svg", 
      "grade-icons/Calculus.svg"
    ], 
    grades: "9-12" 
  },
];

// Animated Icon Display Component
const AnimatedGradeIcons = ({ icons }: { icons: string[] }) => {
  const [currentIconIndex, setCurrentIconIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIconIndex((prevIndex) => (prevIndex + 1) % icons.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [icons.length]);

  return (
    <div className="relative h-[92px] w-[92px] mx-auto flex items-center justify-center">
      {icons.map((icon, index) => (
        <img
          key={icon}
          src={icon}
          alt={`Grade icon ${index + 1}`}
          className={`absolute inset-0 h-[55.2px] w-auto object-contain transition-all duration-500 ease-in-out ${
            index === currentIconIndex
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-75'
          }`}
          style={{
            top: '50%',
            left: '50%',
            transform: index === currentIconIndex 
              ? 'translate(-50%, -50%) scale(1)' 
              : 'translate(-50%, -50%) scale(0.75)'
          }}
        />
      ))}
    </div>
  );
};

const WhatWeSupport = () => {
  return (
    <section className="py-2">
             <style>{`
         @keyframes scroll {
           0% { transform: translateX(0); }
           100% { transform: translateX(-50%); }
         }
         .animate-scroll {
           animation: scroll 20s linear infinite;
         }
         .scroll-container {
           display: flex;
           width: calc(160%);
         }
         .scroll-item {
           flex-shrink: 0;
           padding: 0 1rem;
         }
       `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mt-16 space-y-16">
                     {/* Math Subject Support */}
           <div className="text-center">
             <h2 className="text-2xl font-bold text-gray-900 mb-4">
               Transform your Math's journey
             </h2>
             <div className="flex justify-center mb-4">
               <img 
                 src="course-card-icon/Artboard6.svg"
                 alt="Math Subject Icon" 
                 className="h-36 w-auto object-cover"
               />
             </div>
           </div>

          {/* School Systems */}
          <div>
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                For All School Systems
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive support aligned with your state's curriculum standards
              </p>
            </div>
            
                         {/* Auto-scrolling School Systems */}
             <div className="overflow-hidden">
               <div className="scroll-container animate-scroll">
                 {[...schoolSystems, ...schoolSystems].map((system, index) => (
                   <div key={index} className="scroll-item">
                     <div className="text-center">
                       <img 
                         src={system.icon} 
                         alt={system.name} 
                         className="h-20 w-auto mx-auto object-contain"
                       />
                     </div>
                   </div>
                 ))}
               </div>
             </div>
          </div>

          {/* Grade Levels */}
          <div>
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                In All Grades
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Age-appropriate learning support from kindergarten through high school
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {gradeLevels.map((level) => (
                <Card key={level.name} className="text-center border-0 shadow-none">
                  <CardHeader className="pb-2">
                    <div className="mx-auto h-[92px] w-[92px] rounded-full bg-rfpurple-100 text-rfpurple-600 flex items-center justify-center mb-4">
                      <AnimatedGradeIcons icons={level.icons} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-gray-900">{level.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-lg font-medium text-rfpurple-600">
                      {level.grades}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeSupport;
